import styled from 'styled-components'
import { useStartCheckout } from '../../services/api/subscription.hooks'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { Border, Colors, Spacing } from '../../styles'
import { icons } from '../../styles/images'
import {
    SUBSCRIBE_CHARGE_TEXT,
    daysLeftText,
    formatTrialEndDate,
    hasManageableSubscriptionStatus,
} from '../../utils/subscription'
import { Icon } from '../atoms/Icon'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import { LabelSmall } from '../atoms/typography/Typography'
import { CollapsedIconContainer, CollapsedNavButton } from '../navigation_sidebar/NavigationLink'
import Tip from '../radix/Tip'

const TrialContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
    padding: ${Spacing._8};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.small};
    user-select: none;
`

const TrialButton = styled(NoStyleButton)`
    display: block;
    width: 100%;
    text-align: left;
    &:hover ${TrialContainer} {
        background-color: ${Colors.background.hover};
    }
`

interface TrialIndicatorProps {
    isCollapsed?: boolean
}
const TrialIndicator = ({ isCollapsed = false }: TrialIndicatorProps) => {
    const { data: userInfo } = useGetUserInfo()
    const { mutate: startCheckout, isLoading } = useStartCheckout()

    const hasManageableSubscription = hasManageableSubscriptionStatus(
        userInfo?.subscription_status,
        userInfo?.is_subscribed
    )

    // is_in_trial is purely time-based, so hide the countdown from anyone with a Stripe subscription
    if (!userInfo?.is_in_trial || hasManageableSubscription) return null

    const daysRemaining = userInfo.trial_days_remaining ?? 0
    const trialEndDate = formatTrialEndDate(userInfo.trial_ends_at)
    const label = trialEndDate ? `Free trial · ends ${trialEndDate}` : `Free trial · ${daysLeftText(daysRemaining)}`
    const tipContent = `${daysLeftText(daysRemaining)} in your free trial - ${SUBSCRIBE_CHARGE_TEXT}`

    if (isCollapsed) {
        return (
            <Tip content={tipContent} side="right">
                <CollapsedNavButton
                    type="button"
                    aria-label={label}
                    onClick={() => startCheckout()}
                    disabled={isLoading}
                >
                    <CollapsedIconContainer>
                        <Icon icon={icons.timer} color="gray" />
                    </CollapsedIconContainer>
                </CollapsedNavButton>
            </Tip>
        )
    }

    return (
        <Tip content={tipContent} side="right">
            <TrialButton type="button" onClick={() => startCheckout()} disabled={isLoading}>
                <TrialContainer>
                    <Icon icon={icons.timer} size="small" color="gray" />
                    <LabelSmall color="muted">{label}</LabelSmall>
                </TrialContainer>
            </TrialButton>
        </Tip>
    )
}

export default TrialIndicator
